import { MonthlyIncidentCount } from "../../../types/insightDashboard";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from "../../ui/chart";
import { CHART_MARGIN_TOP } from "../chartStyles";
import { formatNumber } from "../utils";
import { InsightLegendContent } from "./InsightLegendContent";
import { CardNotes } from "../CardNotes";

interface MonthlyComparisonBarChartProps {
  title: string;
  description: string;
  notes?: string[];
  focusData: MonthlyIncidentCount[];
  comparisonData: MonthlyIncidentCount[];
  focusYear: number | string;
  comparisonYear: number | string;
  // What each period is called when that isn't its year, like "Past 6 Months"
  focusLabel?: string;
  comparisonLabel?: string;
}

// Each bar is that month's own total, not a running total. Unlike ComparisonAreaChart, future
// months are drawn as they arrive from the provider, which sends them as 0 (SLG-5208)
export function MonthlyComparisonBarChart({
  title,
  description,
  notes,
  focusData,
  comparisonData,
  focusYear,
  comparisonYear,
  focusLabel = String(focusYear),
  comparisonLabel = String(comparisonYear),
}: MonthlyComparisonBarChartProps) {
  const chartData = focusData.map((focusMonthData, index) => ({
    month: focusMonthData.month,
    focusYearData: focusMonthData.value,
    comparisonYearData: comparisonData[index]?.value ?? null,
  }));

  const chartConfig = {
    focusYearData: {
      label: focusLabel,
      color: "var(--chart-1)",
    },
    comparisonYearData: {
      label: comparisonLabel,
      color: "var(--chart-2)",
    },
  } satisfies ChartConfig;

  return (
    <Card className="tw:h-full">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
        <CardNotes notes={notes} />
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="tw:aspect-auto tw:h-[250px] tw:w-full">
          <BarChart
            accessibilityLayer
            data={chartData}
            margin={{
              top: CHART_MARGIN_TOP,
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value: string) => value.slice(0, 3)}
            />
            <YAxis
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number) => formatNumber(value)}
            />
            <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" />} />
            <Bar dataKey="focusYearData" fill="var(--color-focusYearData)" radius={4} />
            <Bar dataKey="comparisonYearData" fill="var(--color-comparisonYearData)" radius={4} />
            <ChartLegend content={<InsightLegendContent />} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
